import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, ArrowRight, Brain, Globe, TestTube, Plane, TrendingUp, Unlock, Eye } from 'lucide-react';
import { mockData } from '../mock/data';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';

const UseCasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { useCases } = mockData;
  const { t } = useTranslation();
  useSEO('useCases');

  // Function to get tailored benefit descriptions
  const getBenefitDescription = (useCase, benefit) => {
    return t(`useCasePage.benefitDescriptions.${useCase}.${benefit}`) || t('useCasePage.defaultBenefitDescription');
  };

  const iconMap = {
    Brain: Brain,
    Globe: Globe,
    TestTube: TestTube,
    Plane: Plane,
    TrendingUp: TrendingUp,
    Unlock: Unlock,
    Eye: Eye
  };

  // If no slug, show all use cases overview
  if (!slug) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="">
          {/* Hero Section */}
          <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('useCasePage.overview.title1')} <span className="text-cyan-400">{t('useCasePage.overview.title2')}</span>
              </h1>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
                {t('useCasePage.overview.subtitle')}
              </p>
            </div>
          </section>

          {/* Use Cases Grid */}
          <section className="py-16 bg-slate-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {useCases.map((useCaseData, index) => {
                  const IconComponent = iconMap[useCaseData.icon] || Globe;
                  const translatedUseCase = {
                    ...useCaseData,
                    title: t(`useCasePage.useCases.${useCaseData.slug}.title`),
                    description: t(`useCasePage.useCases.${useCaseData.slug}.description`),
                    longDescription: t(`useCasePage.useCases.${useCaseData.slug}.longDescription`),
                    benefits: t(`useCasePage.useCases.${useCaseData.slug}.benefits`),
                    originalBenefits: useCaseData.benefits
                  };
                  
                  return (
                    <div
                      key={index}
                      className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 hover:border-cyan-500/50 hover:bg-slate-900/50 transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-8 h-8 text-cyan-400" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                        {translatedUseCase.title}
                      </h3>
                      
                      <p className="text-slate-400 leading-relaxed mb-6">
                        {translatedUseCase.longDescription}
                      </p>

                      <ul className="space-y-2 mb-6">
                        {translatedUseCase.benefits.map((benefit, bIndex) => (
                          <li key={bIndex} className="flex items-center text-sm text-slate-300">
                            <Check className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      
                      <Link to={`/use-cases/${useCaseData.slug}`}>
                        <Button className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                          {t('useCasePage.overview.learnMore')}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // Find specific use case
  const useCaseData = useCases.find(uc => uc.slug === slug);
  
  // Get translated use case data
  const useCase = useCaseData ? {
    ...useCaseData,
    title: t(`useCasePage.useCases.${slug}.title`),
    description: t(`useCasePage.useCases.${slug}.description`),
    longDescription: t(`useCasePage.useCases.${slug}.longDescription`),
    benefits: t(`useCasePage.useCases.${slug}.benefits`),
    originalBenefits: useCaseData.benefits // Keep original English benefits for description lookup
  } : null;
  
  if (!useCaseData) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-6">{t('useCasePage.notFound.title')}</h1>
            <p className="text-slate-400 mb-8">{t('useCasePage.notFound.description')}</p>
            <Button onClick={() => navigate('/use-cases')}>
              {t('useCasePage.notFound.goBack')}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[useCase.icon] || Globe;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-3xl mb-8 mx-auto">
                <IconComponent className="w-10 h-10 text-cyan-400" />
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {useCase.title}
              </h1>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                {useCase.longDescription}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a 
                href="https://dashboard.ip9.io" 
                className="inline-block"
              >
                <Button 
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4 text-lg"
                >
                  {t('useCasePage.startFreeTrial')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              
              <Link to="/contact" className="inline-block">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4 text-lg"
                >
                  {t('useCasePage.contactSales')}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              {t('useCasePage.keyBenefitsFor')} <span className="text-cyan-400">{useCase.title}</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {useCase.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg flex items-center justify-center mt-1">
                    <Check className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit}</h3>
                    <p className="text-slate-400">
                      {getBenefitDescription(useCase.slug, useCase.originalBenefits[index])}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              {t('useCasePage.whyIP9For')} <span className="text-cyan-400">{useCase.title}</span>?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{t('useCasePage.features.globalCoverage.title')}</h3>
                <p className="text-slate-400">{t('useCasePage.features.globalCoverage.description')}</p>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{t('useCasePage.features.highSuccessRate.title')}</h3>
                <p className="text-slate-400">{t('useCasePage.features.highSuccessRate.description')}</p>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ArrowRight className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{t('useCasePage.features.easyIntegration.title')}</h3>
                <p className="text-slate-400">{t('useCasePage.features.easyIntegration.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-12">
              <h2 className="text-3xl font-bold text-white mb-6">
                {t('useCasePage.cta.title')} {useCase.title}?
              </h2>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                {t('useCasePage.cta.subtitle')} {useCase.title.toLowerCase()} {t('useCasePage.cta.solutions')}.
              </p>
              <div className="space-x-4">
                <a 
                  href="https://dashboard.ip9.io" 
                  className="inline-block"
                >
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4">
                    {t('useCasePage.startFreeTrial')}
                  </Button>
                </a>
                <Link to="/pricing" className="inline-block">
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4">
                    {t('useCasePage.viewPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default UseCasePage;